import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useI18n, useLocalized } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import { useAddToCart, getGetCartQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { getCartSessionId } from "@/lib/cart";
import { ShoppingCart, Heart, Minus, Plus, Star, ExternalLink } from "lucide-react";
import { useWishlist } from "@/lib/wishlist-context";
import { useTheme } from "@/lib/theme-context";
import { useToast } from "@/hooks/use-toast";

const BASE_URL = import.meta.env.BASE_URL || "/";

interface QuickViewProps {
  product: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuickView({ product, open, onOpenChange }: QuickViewProps) {
  const { lang } = useI18n();
  const theme = useTheme();
  const l = useLocalized();
  const { formatPrice } = useCurrency();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const sessionId = getCartSessionId();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const [quantity, setQuantity] = useState(1);
  const [mainImage, setMainImage] = useState<string>("");
  const [options, setOptions] = useState<any[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});

  const addToCart = useAddToCart({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetCartQueryKey({ sessionId }) });
        toast({ title: lang === "ar" ? "تمت الإضافة للسلة" : "Added to cart" });
        if (theme?.cartOpenOnAdd === "true") {
          import("@/lib/cart").then(m => m.openSideCart());
        }
      },
      onError: (e: any) => {
        // Surface the actual server error (e.g. flash-sale limit messages)
        // — the generated client throws ApiError with the parsed body on
        // `e.data`, not on `e.response.data`. Reject HTML error pages
        // (Express default 500) and generic status text so the user
        // sees a clean Arabic message instead of raw HTML.
        let msg = lang === "ar" ? "حدث خطأ أثناء الإضافة، حاول مرة أخرى" : "Error adding to cart, please try again";
        try {
          const data = e?.data ?? e?.response?.data ?? null;
          let serverMsg = "";
          if (data && typeof data === "object" && (data as any).error) {
            serverMsg = String((data as any).error);
          } else if (typeof data === "string") {
            serverMsg = data;
          } else if (typeof e?.message === "string" && !/^Request failed/i.test(e.message)) {
            serverMsg = e.message;
          }
          const looksLikeHtml = /<!DOCTYPE|<html|<head|<body|<pre|<\/[a-z]+>/i.test(serverMsg);
          const isGeneric = /^\s*(internal server error|bad gateway|service unavailable|not found|failed with status)/i.test(serverMsg);
          if (!looksLikeHtml && !isGeneric && serverMsg && serverMsg.length > 0 && serverMsg.length < 300) {
            msg = serverMsg;
          }
        } catch {}
        toast({ variant: "destructive", description: msg });
      },
    },
  });

  useEffect(() => {
    if (product) {
      setMainImage(product.imageUrl || "");
      setQuantity(1);
      setSelectedOptions({});
      setOptions([]);
    }
  }, [product?.id]);

  // Lazy-load options ONLY when the dialog actually opens. Previously this
  // ran on mount for every QuickView in the page, causing N+1 storms (20+
  // parallel /options calls per storefront page) that exhausted the DB pool.
  useEffect(() => {
    if (!open || !product) return;
    let cancelled = false;
    fetch(`${BASE_URL}api/products/${product.id}/options`)
      .then(r => r.json())
      .then(data => { if (!cancelled && Array.isArray(data)) setOptions(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, product?.id]);

  if (!product) return null;

  const images = [...new Set([product.imageUrl, ...(product.images || [])].filter(Boolean))];
  const inStock = (product.quantity || 0) > 0;
  const discountPercent = product.salePrice
    ? Math.round((1 - product.salePrice / product.price) * 100)
    : 0;

  const handleAddToCart = () => {
    addToCart.mutate({
      data: {
        sessionId,
        productId: product.id,
        quantity,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-white border-0 rounded-none sm:rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          <div className="bg-gray-50 p-4">
            <div
              className="aspect-square relative overflow-hidden mb-3"
              style={{ borderRadius: "var(--theme-product-image-radius, var(--radius, 0.5rem))" }}
            >
              {mainImage ? (
                <img src={mainImage} alt={l(product, "name")} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300"></div>
              )}
              {product.salePrice && (
                <div className="absolute top-3 start-3 bg-red-500 text-white text-[10px] font-bold px-2.5 py-1 rounded">
                  -{discountPercent}%
                </div>
              )}
              {!inStock && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <span className="bg-white text-black text-xs font-medium px-4 py-2">
                    {lang === "ar" ? "نفذت الكمية" : "Out of Stock"}
                  </span>
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {images.slice(0, 5).map((img: string, i: number) => (
                  <button
                    key={i}
                    onClick={() => setMainImage(img)}
                    className={`w-14 h-14 shrink-0 overflow-hidden border-2 transition-colors ${
                      mainImage === img ? "border-black" : "border-transparent hover:border-gray-300"
                    }`}
                  >
                    <img src={img} alt={`${l(product, "name")} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="p-6 flex flex-col">
            {product.brandName && (
              <p className="text-[10px] tracking-[0.2em] uppercase text-gray-400 mb-1">{product.brandName}</p>
            )}
            <h2 className="text-lg font-medium leading-tight mb-3">{l(product, "name")}</h2>

            {product.avgRating > 0 && (
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} className={`h-3.5 w-3.5 ${s <= Math.round(product.avgRating) ? "fill-amber-400 text-amber-400" : "text-gray-200"}`} />
                ))}
                <span className="text-xs text-gray-400 ms-1">({product.reviewCount})</span>
              </div>
            )}

            <div className="flex items-baseline gap-2 mb-4">
              {product.salePrice ? (
                <>
                  <span className="text-xl font-medium text-red-600">{formatPrice(product.salePrice)}</span>
                  <span className="text-sm text-gray-400 line-through">{formatPrice(product.price)}</span>
                  <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded font-medium">
                    -{discountPercent}%
                  </span>
                </>
              ) : (
                <span className="text-xl font-medium">{formatPrice(product.price)}</span>
              )}
            </div>

            {product.shortDescription && (
              <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-3">
                {lang === "ar" ? (product.shortDescriptionAr || product.shortDescription) : product.shortDescription}
              </p>
            )}

            {options.length > 0 && (
              <div className="space-y-3 mb-4">
                {options.map((opt: any) => (
                  <div key={opt.id}>
                    <p className="text-xs font-medium mb-1.5">{lang === "ar" ? (opt.nameAr || opt.name) : opt.name}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(opt.values || []).map((val: any) => (
                        <button
                          key={val.id}
                          onClick={() => setSelectedOptions({ ...selectedOptions, [opt.name]: val.value })}
                          className={`px-3 py-1.5 text-xs border transition-colors ${
                            selectedOptions[opt.name] === val.value
                              ? "border-black bg-black text-white"
                              : "border-gray-200 hover:border-gray-400"
                          }`}
                        >
                          {val.value}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-auto space-y-3">
              {inStock && (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">{lang === "ar" ? "الكمية:" : "Qty:"}</span>
                  <div className="flex items-center border border-gray-200">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-2 hover:bg-gray-50">
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="px-4 text-sm font-medium min-w-[40px] text-center">{quantity}</span>
                    <button onClick={() => setQuantity(quantity + 1)} className="p-2 hover:bg-gray-50">
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                  <span className="text-[10px] text-green-600">
                    {lang === "ar" ? `${product.quantity} متوفر` : `${product.quantity} available`}
                  </span>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={handleAddToCart}
                  disabled={!inStock || addToCart.isPending}
                  className="btn-theme-primary flex-1 text-xs tracking-[0.15em] uppercase py-5"
                >
                  <ShoppingCart className="h-4 w-4 me-2" />
                  {addToCart.isPending
                    ? (lang === "ar" ? "جاري الإضافة..." : "Adding...")
                    : (lang === "ar" ? "أضف للسلة" : "Add to Cart")}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-none border-gray-200 h-[42px] w-[42px] shrink-0"
                  onClick={() => toggleWishlist(product.id)}
                >
                  <Heart className={`h-4 w-4 ${isInWishlist(product.id) ? "fill-red-500 text-red-500" : ""}`} />
                </Button>
              </div>

              <Link
                href={`/product/${product.id}`}
                onClick={() => onOpenChange(false)}
                className="flex items-center justify-center gap-1.5 text-xs text-gray-500 hover:text-black transition-colors py-1"
              >
                <ExternalLink className="h-3 w-3" />
                {lang === "ar" ? "عرض التفاصيل الكاملة" : "View Full Details"}
              </Link>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
